import React, { useEffect } from "react"

import { useLocation } from "react-router"
import { gql, useQuery } from "urql"

import { H3, WithLoadingIndicator } from "~tailwindui/Basics"

import CalendarInfo from "./CalendarInfoModal"
import QuickLinks from "./QuickLinks"
import SessionTimeline from "./SessionTimeline"
import ToDo from "./ToDo"
import TutoringOpportunities from "./TutoringOpportunities"
import YourAccount from "./YourAccount"

const TutorDashboard = () => {
  const { search } = useLocation()
  const calendarModalOpen = new URLSearchParams(search).get(
    "calendar_instructions"
  )

  const [result, refetchQuery] = useQuery({ query: tutorQuery })

  useEffect(() => {
    if (!result.data) return

    const timerId = setTimeout(() => {
      if (document.visibilityState === "visible") {
        refetchQuery()
      }
    }, 15 * 60 * 1000)

    return () => clearTimeout(timerId)
  }, [result.data, refetchQuery])

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refetchQuery()
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange)

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [refetchQuery])

  return (
    <WithLoadingIndicator result={result}>
      {({ data: { viewer } }) => (
        <div>
          {calendarModalOpen && viewer.calendarId && (
            <CalendarInfo calendarId={viewer.calendarId} />
          )}

          <div className="w-full md:grid md:grid-cols-5 md:gap-4 lg:gap-12">
            <QuickLinks ssoEmail={viewer.ssoEmail} />
          </div>

          <hr className="my-4" />

          <div className="w-full md:grid md:grid-cols-5 md:gap-4 lg:gap-12">
            <div className="space-y-3 md:col-span-3 md:space-y-5">
              <ToDo viewer={viewer} />
              <TutoringOpportunities viewer={viewer} />
              <YourAccount viewer={viewer} />
            </div>

            <div className="mt-4 space-y-4 md:col-span-2 md:mt-0">
              <H3>Upcoming Sessions</H3>
              <SessionTimeline sessions={viewer.scheduledSessions.data} />
            </div>
          </div>
        </div>
      )}
    </WithLoadingIndicator>
  )
}

const tutorQuery = gql`
  query TutorDashboard {
    viewer {
      id
      firstName
      lastName
      ssoEmail
      calendarId
      mayCreateExpense
      availableStudyGroupReplacements {
        id
      }
      unbilledSessions: sessions(
        filters: { past: true, billState: unbilled, statusState: scheduled }
      ) {
        data {
          id
        }
      }
      groupsNeedingScheduling: studyGroups(needingScheduling: true) {
        data {
          id
        }
      }
      openGenerationInvites {
        availableStudyGroups {
          id
        }
      }
      registerableCourseClusters {
        id
      }
      scheduledSessions: sessions(
        filters: { future: true, statusState: scheduled, billState: unbilled }
      ) {
        totalPages
        data {
          id
          startsAt
          endsAt
          cloudroomActive
          realtimeboardPending
          realtimeboards {
            id
          }
          subject {
            name
          }
          studyGroup {
            id
            name
          }
          students {
            id
            fullName
          }
        }
      }
      requestedSessions {
        id
      }
    }
  }
`

export default TutorDashboard
